import ContainerWrapper from "@components/BasicComponents/Wrappers/ContainerWrapper/ContainerWrapper"
import {HeroModuleType} from "@hooks/WorkWithJson/GetComponentDataByKey/GetComponentDataByKeyTypes"
import {clsx} from "clsx"
import {AnimatePresence, motion} from "framer-motion"
import React, {useRef, useState} from "react"

import HeroCurveBackground from "./HeroCurveBackground"
import HeroSlideContent from "./HeroSlideContent"
import styles from "./HomeHeroAnimated.module.scss"
import {useSvgHeight} from "./useSvgHeight"

interface HomeHeroAnimatedProps {
	data: HeroModuleType[]
	className?: string
	textColor?: "black" | "white"
	titleStyle?: string
}

const HomeHeroAnimated: React.FC<HomeHeroAnimatedProps> = ({
	data,
	className,
	textColor = "black",
	titleStyle = "",
}) => {
	const {ref} = useSvgHeight()
	const [currentIndex, setCurrentIndex] = useState(0)
	const isFirstRender = useRef(true)

	const colors = [
		"linear-gradient(253deg, #BBD2FF 7.81%, #F1F5FF 89.34%)",
		"linear-gradient(251deg, #175EEB 7.59%, #92B6FF 96.07%)",
		"linear-gradient(221deg, #BE01BD 19.34%, #FFC0FF 82.28%)",
	]

	const currentColor = colors[currentIndex % colors.length]

	return (
		<ContainerWrapper>
			<div style={{lineHeight: 0, position: "relative"}}>
				<div
					className={clsx(
						styles.mainBackground,
						className,
						data[currentIndex].backgroundVideoDesktop &&
							styles.backgroundTransparent,
					)}
				>
					<HeroSlideContent
						heroItem={data[currentIndex]}
						currentIndex={currentIndex}
						textColor={textColor}
						titleStyle={titleStyle}
						backgroundColor={currentColor}
					/>

					<div
						style={{
							position: "absolute",
							width: 0,
							height: 0,
							overflow: "hidden",
							opacity: 0,
							pointerEvents: "none",
						}}
						aria-hidden={"true"}
					>
						{data.map((item, index) => {
							if (index === currentIndex) return null
							const bgColor = colors[index % colors.length]
							return (
								<HeroSlideContent
									key={index}
									heroItem={item}
									currentIndex={index}
									textColor={textColor}
									titleStyle={titleStyle}
									backgroundColor={bgColor}
								/>
							)
						})}
					</div>

					<HeroCurveBackground
						backgroundVideoDesktop={
							data[currentIndex].backgroundVideoDesktop
						}
						refProp={ref}
						backgroundColor={currentColor}
						slideCount={data.length}
						activeIndex={currentIndex}
						autoDuration={5000}
						onChange={(index) => setCurrentIndex(index)}
					/>

					<AnimatePresence mode={"sync"}>
						<motion.div
							key={currentColor}
							className={`${
								currentColor ? styles.backgroundTransparent : ""
							} ${styles.beforeBottomWhite}`}
							style={{
								position: "absolute",
								inset: 0,
								backgroundImage: currentColor,
								backgroundSize: "cover",
								backgroundPosition: "center",
							}}
							initial={{opacity: isFirstRender.current ? 1 : 0}}
							animate={{opacity: 1}}
							exit={{opacity: 0}}
							transition={{
								duration: isFirstRender.current ? 0 : 0.8,
								ease: "easeInOut",
							}}
							onAnimationComplete={() => {
								isFirstRender.current = false
							}}
						/>
					</AnimatePresence>
				</div>
			</div>
		</ContainerWrapper>
	)
}

export default HomeHeroAnimated
